import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Check, Loader2, MoonStar } from "lucide-react";
import { api, type BlackoutResolveJobView, type SocialPlatform } from "../../lib/api.js";
import { useDismiss } from "../../components/ui/useDismiss.js";

/**
 * Posts that are ALREADY scheduled inside Shabbat, a chag or a memorial day.
 *
 * New times cannot land there any more; these were placed before the rule
 * existed. Nothing moves until the operator ticks it (decided 2026-09-19), and
 * each row says where it would go: the same hour, on the next allowed day.
 * A post Postiz holds is withdrawn and re-created there, as any date edit is.
 */

const PLATFORM_HE: Record<SocialPlatform, string> = {
  facebook: "פייסבוק", instagram: "אינסטגרם", tiktok: "טיקטוק", youtube: "יוטיוב",
};

const humanIl = (iso: string) =>
  new Date(iso).toLocaleString("he-IL", { timeZone: "Asia/Jerusalem", weekday: "short", day: "numeric", month: "numeric", hour: "2-digit", minute: "2-digit", hourCycle: "h23" });

export function BlackoutConflictsDialog({ onClose, onDone }: { onClose: () => void; onDone: () => void }) {
  const dismiss = useDismiss(onClose);
  const list = useQuery({ queryKey: ["blackout-conflicts"], queryFn: () => api.blackoutConflicts(60) });
  const conflicts = list.data?.conflicts ?? [];
  const reviews = list.data?.reviews ?? [];
  const [picked, setPicked] = useState<Set<number> | null>(null);
  const [job, setJob] = useState<BlackoutResolveJobView | null>(null);

  // Everything ticked by default once the list arrives: the reason to open this
  // dialog is to move them.
  useEffect(() => {
    if (picked === null && list.data) setPicked(new Set(list.data.conflicts.map((c) => c.variantId)));
  }, [list.data, picked]);

  // Poll while the move runs: each Postiz post is a withdraw + re-create.
  useEffect(() => {
    if (job?.status !== "running") return;
    const t = setInterval(() => {
      void api.blackoutResolveStatus().then((r) => { if (r.job) setJob(r.job); }).catch(() => {});
    }, 1500);
    return () => clearInterval(t);
  }, [job?.status]);

  const run = useMutation({
    mutationFn: () => api.resolveBlackoutConflicts([...(picked ?? [])]),
    onSuccess: (r) => setJob(r.job),
  });

  const toggle = (id: number) =>
    setPicked((prev) => {
      const next = new Set(prev ?? []);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const running = run.isPending || job?.status === "running";
  const finished = job?.status === "done";
  const resultOf = (id: number) => job?.results.find((r) => r.variantId === id);

  return (
    <div {...dismiss} className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-xl bg-white shadow-xl">
        <header className="flex items-center justify-between border-b border-line px-4 py-3">
          <h2 className="flex items-center gap-1.5 text-sm font-semibold"><MoonStar size={15} /> לוח השנה: פוסטים בזמן חסום ולבדיקה</h2>
          <button onClick={finished ? onDone : onClose} disabled={running} className="text-sm text-muted hover:text-ink disabled:opacity-40">סגירה</button>
        </header>

        <div className="grow space-y-3 overflow-auto p-4 text-sm">
          <p className="rounded-lg bg-canvas px-3 py-2 text-[11px] text-muted">
            לא מפרסמים משעתיים לפני הדלקת נרות ועד שעה אחרי צאת החג/השבת. הפוסטים האלה תוזמנו לפני שהכלל נכנס.
            העברה שומרת על <strong className="text-ink">אותה שעה</strong> ביום המותר הבא. פוסט שכבר בפוסטיז יימשך וייווצר מחדש בזמן החדש.
          </p>

          {list.isPending && <p className="flex items-center gap-2 text-xs text-muted"><Loader2 size={13} className="animate-spin" /> בודק…</p>}
          {list.isError && <p className="text-xs text-danger">{(list.error as Error).message}</p>}
          {list.data && conflicts.length === 0 && <p className="text-xs text-muted">אין פוסטים בזמן חסום ב-60 הימים הקרובים.</p>}
          {/* Not moved, only shown: a caption naming a holiday it does not land
              in, or a post on a memorial eve. The fix is an edit, by a person. */}
          {reviews.length > 0 && (
            <div className="rounded-lg bg-amber-50 px-3 py-2 text-[11px] text-amber-900">
              <p className="mb-1 font-medium">לבדיקה ידנית ({reviews.length}): התזמון מותר, אבל כדאי לקרוא את הטקסט</p>
              <ul className="space-y-0.5">
                {reviews.map((r) => (
                  <li key={`${r.variantId}-${r.kind}`}>
                    {humanIl(r.scheduledFor)} · {PLATFORM_HE[r.platform] ?? r.platform} {r.format} · {r.label ?? `#${r.variantId}`} — <strong>{r.why}</strong>
                  </li>
                ))}
              </ul>
            </div>
          )}

          {conflicts.map((c) => {
            const r = resultOf(c.variantId);
            return (
              <label key={c.variantId} className="flex items-start gap-2 rounded-lg border border-line px-3 py-2">
                <input
                  type="checkbox"
                  checked={picked?.has(c.variantId) ?? false}
                  onChange={() => toggle(c.variantId)}
                  disabled={running || finished}
                  className="mt-0.5"
                />
                <span className="min-w-0 grow">
                  <span className="block truncate text-xs font-medium">{c.label ?? `#${c.variantId}`}</span>
                  <span className="block text-[11px] text-muted">
                    {PLATFORM_HE[c.platform] ?? c.platform} · {c.format} · {c.inPostiz ? "בפוסטיז" : "אצלנו בלבד"}
                  </span>
                  <span className="block text-[11px]">
                    <span className="text-danger">{humanIl(c.scheduledFor)} · {c.window.labelHe}</span>
                    {" ← "}
                    <span className="font-medium text-emerald-800">{humanIl(c.suggested)}</span>
                  </span>
                </span>
                {r && (r.ok
                  ? <span className="shrink-0 text-[11px] font-medium text-emerald-700">הועבר ✓</span>
                  : <span className="shrink-0 text-[11px] font-medium text-danger" title={r.note}>{r.note ?? "נכשל"}</span>)}
              </label>
            );
          })}

          {run.isError && <p className="text-xs text-danger">{(run.error as Error).message}</p>}
          {finished && (
            <p className="flex items-center gap-1 rounded-lg bg-emerald-50 px-3 py-2 text-xs font-medium text-emerald-900">
              <Check size={13} /> הועברו {job!.results.filter((r) => r.ok && r.to).length} מתוך {job!.total}.
            </p>
          )}
        </div>

        <footer className="flex items-center gap-2 border-t border-line px-4 py-3">
          <span className="text-xs text-muted">
            {running && job ? `מעביר ${job.results.length} מתוך ${job.total}…` : `${picked?.size ?? 0} מסומנים`}
          </span>
          <span className="grow" />
          {finished ? (
            <button onClick={onDone} className="rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white">סגירה ורענון</button>
          ) : (
            <button
              onClick={() => run.mutate()}
              disabled={!picked || picked.size === 0 || running}
              className="flex items-center gap-1.5 rounded-lg bg-brand-700 px-3 py-2 text-sm font-medium text-white disabled:opacity-40"
            >
              {running && <Loader2 size={14} className="animate-spin" />}
              העבר ({picked?.size ?? 0})
            </button>
          )}
        </footer>
      </div>
    </div>
  );
}
